import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  useGetAssetPublishesQuery,
  useGetPromoChannelsQuery,
  useGetReleaseKitQuery,
} from '../../../../redux/api/fymAPI';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { buildKitSlots } from '../../promote/releases/kit/kitSlots';
import { openPublishDialog } from '../../promote/publish/publishDialogStore';
import { downloadMediaUrl } from '../../promote/inlineMediaUrl';
import {
  youtubeExtraItems,
  youtubeLoginOf,
  type YouTubeExtraAction,
  type YouTubeExtraItemView,
} from './youtubeExtraRules';
import { youtubeItemTitle } from './youtubeExtraTexts';

/**
 * The data and the actions of "YouTube extras": the full-song videos of the release kit, their
 * YouTube records and the YouTube channel. Upload, Replace and Try again open the existing
 * PublishDialog (`POST .../asset-publishes`: the made-for-kids answer and the time are asked there,
 * as before). The socket events `kit.asset.updated` and `asset.publish.updated` read the data again.
 */
export function useYouTubeExtras({
  accountId,
  item,
  readOnly,
}: {
  accountId: number;
  item: CatalogItem;
  readOnly: boolean;
}) {
  const intl = useIntl();
  const router = useSMLRouter();
  const { canRenderFullSongVideos } = useMusic24Capabilities();
  const { data: release } = useGetReleaseKitQuery({ accountId, catalogItemUuid: item.uuid });
  const { data: publishData } = useGetAssetPublishesQuery({ accountId, catalogItemUuid: item.uuid });
  const { data: channelData } = useGetPromoChannelsQuery({ accountId }, { skip: readOnly });
  const login = useMemo(
    () => youtubeLoginOf(channelData?.channels?.find((channel) => channel.platform === 'youtube')),
    [channelData]
  );
  const slots = useMemo(
    () =>
      buildKitSlots({
        item,
        // An older or partial answer may lack a list: read it as empty.
        release: { assets: release?.assets ?? [], newest: release?.newest ?? [] },
        canRenderFullSongVideos,
      }),
    [canRenderFullSongVideos, item, release]
  );
  const items = useMemo(
    () =>
      youtubeExtraItems({
        slots,
        publishes: publishData?.publishes ?? [],
        catalogItemUuid: item.uuid,
        login,
        readOnly,
        now: new Date(),
      }),
    [item.uuid, login, publishData, readOnly, slots]
  );

  const run = useCallback(
    (action: YouTubeExtraAction, extra: YouTubeExtraItemView) => {
      const openDialog = (kitAssetUuid: string, fileUrl: string | null) =>
        openPublishDialog({
          accountId,
          kitAssetUuid,
          catalogItemUuid: item.uuid,
          channel: 'youtube',
          assetName: youtubeItemTitle(intl, extra),
          downloadUrl: fileUrl === null ? null : downloadMediaUrl(fileUrl),
        });
      switch (action) {
        case 'connect':
        case 'reconnect':
          router.push(SMLRoutes.promoteChannels);
          return;
        case 'makeIt':
          router.push(SMLRoutes.promoteStudio, { catalogItemUuid: item.uuid, theme: extra.themeId ?? 'musicVideo' });
          return;
        case 'upload':
        case 'replace':
        case 'retry':
        case 'uploadAgain':
          if (extra.readyAsset !== null) {
            openDialog(extra.readyAsset.uuid, extra.readyAsset.fileUrl);
          }
          return;
        case 'status':
        case 'changeTime':
          if (extra.record !== null) {
            openDialog(extra.record.kitAssetUuid, extra.readyAsset?.fileUrl ?? null);
          }
          break;
        case 'open':
          // An address: the item draws it as a link, and the browser opens it.
          break;
      }
    },
    [accountId, intl, item.uuid, router]
  );

  return { items, run };
}
